<script setup lang="ts">
import { RestaurantAreas, RestaurantAreaType } from '~/lib/interfaces/restaurant'

const { t } = useLocale()
const appStore = useAppStore()
const { trackClickArea } = useGTM()

const props = defineProps<{
  candidateArea?: RestaurantAreaType
  areaSelected?: RestaurantAreaType
}>()

const emit = defineEmits(['setArea'])

const contentMOP = { tKey: 'onboarding.stepMOP', action: true, isLast: false }
const contentDLV = { tKey: 'onboarding.stepDLV', action: true, isLast: false }

const canChangeArea = computed(() => !!appStore.initialFetchLoaded)
const mopActive = computed(() => !!appStore.mdwConfig?.mopActive)
const dlvActive = computed(() => !!appStore.mdwConfig?.dlvActive)

const setArea = ({
  area,
  updateGoogleMaps,
}: {
  area: RestaurantAreaType
  updateGoogleMaps?: boolean
}) => {
  trackClickArea(area)
  emit('setArea', { area, updateGoogleMaps })
}

onMounted(() => {
  if (!props.candidateArea && props.areaSelected) {
    setArea({
      area: props.areaSelected,
      updateGoogleMaps: true,
    })
  }
})
</script>

<template>
  <div
    v-if="mopActive || dlvActive"
    data-test="toggle-search"
    class="mb-10 mt-6 flex justify-center gap-3 text-sm font-bold leading-4 md:mb-4 md:justify-start md:gap-2"
  >
    <PopperWrapper data-popper="stepMOP" placement="bottom">
      <button
        v-if="mopActive"
        data-test="toggle-search--mop"
        class="relative flex h-10 w-[152px] items-center justify-center gap-2 rounded-lg bg-white shadow-elevation hover:scale-105"
        :class="{
          'border-2 border-mcd-main': candidateArea && candidateArea === RestaurantAreas.MOP,
          'hover:!scale-100 hover:opacity-90': !canChangeArea,
        }"
        :disabled="!canChangeArea"
        @click="setArea({ area: RestaurantAreas.MOP, updateGoogleMaps: true })"
      >
        <!-- Loading spinner while loading initial fetch  -->
        <img
          v-if="!canChangeArea"
          src="/images/icons/commons/mcd-spinner.svg"
          alt="spinner"
          class="h-4 w-4 animate-spin"
        />
        <!-- MOP icon  -->
        <img v-else src="/images/icons/pickUp.svg" alt="PickUp" class="h-5" />
        {{ t('common.MOP') }}
        <img
          v-if="candidateArea && candidateArea === RestaurantAreas.MOP"
          class="absolute -right-2 -top-2 h-auto w-4"
          src="/images/icons/store-check.svg"
          alt="selected"
        />
      </button>
      <template #content="{ close }">
        <McdPopper :close="close" :content="contentMOP" />
      </template>
    </PopperWrapper>
    <PopperWrapper data-popper="stepDLV" placement="bottom">
      <button
        v-if="dlvActive"
        id="onboarding-step-3"
        data-test="toggle-search--dlv"
        class="relative flex h-10 w-[152px] items-center justify-center gap-2 rounded-lg bg-white shadow-elevation hover:scale-105"
        :class="{
          'border-2 border-mcd-main': candidateArea && candidateArea === RestaurantAreas.DLV,
          'hover:!scale-100 hover:opacity-90': !canChangeArea,
        }"
        :disabled="!canChangeArea"
        @click="setArea({ area: RestaurantAreas.DLV, updateGoogleMaps: true })"
      >
        <!-- Loading spinner while loading initial fetch  -->
        <img
          v-if="!canChangeArea"
          src="/images/icons/commons/mcd-spinner.svg"
          alt="spinner"
          class="h-4 w-4 animate-spin"
        />
        <!-- DLV icon  -->
        <img v-else src="/images/icons/delivery.svg" alt="Delivery" class="h-5" />
        {{ t('common.DLV') }}
        <img
          v-if="candidateArea && candidateArea === RestaurantAreas.DLV"
          class="absolute -right-2 -top-2 h-auto w-4"
          src="/images/icons/store-check.svg"
          alt="selected"
        />
      </button>
      <template #content="{ close }">
        <McdPopper :close="close" :content="contentDLV" />
      </template>
    </PopperWrapper>
  </div>
</template>
